import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { API } from "@/config";

export default function ServicesPanel({ headers }) {
  const [services, setServices] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", price: "", desc: "" });
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios
      .get(`${API}/services`)
      .then((res) => setServices(res.data))
      .catch(() => toast.error("No se pudieron cargar los servicios"));

  useEffect(() => {
    load();
  }, []);

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name, price: s.price, desc: s.desc });
  };

  const save = async (id) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/services/${id}`, form, { headers });
      setServices((prev) => prev.map((s) => (s.id === id ? res.data : s)));
      setEditingId(null);
      toast.success("Servicio actualizado en tu web");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="admin-services-panel">
      <h1 className="font-display text-2xl sm:text-3xl font-semibold text-obsidian">Servicios y precios</h1>
      <p className="text-sm text-taupe mt-1">
        Los cambios se ven al instante en la sección Servicios de tu web.
      </p>

      {services === null ? (
        <div className="grid gap-4 mt-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 rounded-2xl bg-sand/50 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 mt-6">
          {services.map((s) => (
            <article
              key={s.id}
              data-testid={`service-row-${s.id}`}
              className="rounded-2xl bg-creamcard border border-sand p-5 sm:p-6"
            >
              {editingId === s.id ? (
                <div className="grid gap-3">
                  <div className="grid sm:grid-cols-[1fr_140px] gap-3">
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid={`service-edit-name-${s.id}`}
                      className="rounded-xl border border-sand bg-cream px-4 py-2.5 text-sm text-obsidian focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                    <input
                      type="text"
                      value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })}
                      placeholder="25€"
                      data-testid={`service-edit-price-${s.id}`}
                      className="rounded-xl border border-sand bg-cream px-4 py-2.5 text-sm text-obsidian focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                  </div>
                  <textarea
                    value={form.desc}
                    onChange={(e) => setForm({ ...form, desc: e.target.value })}
                    rows={2}
                    data-testid={`service-edit-desc-${s.id}`}
                    className="rounded-xl border border-sand bg-cream px-4 py-2.5 text-sm text-obsidian focus:outline-none focus:ring-2 focus:ring-gold resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => save(s.id)}
                      disabled={saving}
                      data-testid={`service-save-btn-${s.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-gold hover:bg-gold-dark disabled:opacity-50 text-white text-xs font-medium px-4 py-2 transition-colors"
                    >
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      Guardar
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      data-testid={`service-cancel-btn-${s.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-sand text-taupe hover:text-obsidian text-xs font-medium px-4 py-2 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium text-obsidian">{s.name}</p>
                    <p className="text-xs text-taupe mt-0.5 truncate">{s.desc}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span
                      data-testid={`service-price-${s.id}`}
                      className="font-display text-xl font-semibold text-gold"
                    >
                      {s.price}
                    </span>
                    <button
                      onClick={() => startEdit(s)}
                      data-testid={`service-edit-btn-${s.id}`}
                      className="rounded-full border border-sand text-taupe hover:text-gold hover:border-gold/50 p-2 transition-colors"
                      aria-label="Editar servicio"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
